// INC2-INTAKE (build order increment 2 A, §3 "One intake", "Handler-DE routing"; integrations
// design §2.4, §2.5, §2.9): the row contracts of the one intake and the integration work queue.
// No payload is ever in these rows' contracts: an inbound event's payload is data the handler
// reads from its task file, a job's payload is internal, the ledger never holds a body.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  InboundEventStatus,
  InboundHandlerVia,
  InboundRuleKind,
  InboundSourceKind,
  InboundVerification,
  IntegrationErrorClass,
  IntegrationJobKind,
  IntegrationJobState,
  IntegrationSubscriptionKind,
  IntegrationSubscriptionState,
  NotifyPreset,
} from "./enums";
import { LIMITS, Timestamp, Uuid } from "./primitives";
import { InboundDeliveryScreen } from "./rows";

const stamps = { created_at: Timestamp, updated_at: Timestamp };

/** One way a vendor tells us of changes (integrations §2.4); the sealed secret never leaves. */
export const IntegrationSubscription = z
  .object({
    company_id: Uuid,
    id: Uuid,
    connection_id: Uuid.nullable(),
    owner_user_id: Uuid.nullable(),
    provider: z.string(),
    resource: z.string(),
    kind: IntegrationSubscriptionKind,
    preset: NotifyPreset,
    source_kind: InboundSourceKind,
    source_key: z.string(),
    account_key: z.string().nullable(),
    external_id: z.string().nullable(),
    external_resource_id: z.string().nullable(),
    expires_at: Timestamp.nullable(),
    cursor: z.string().nullable(),
    poll_interval_sec: z.int().nullable(),
    state: IntegrationSubscriptionState,
    last_notice_at: Timestamp.nullable(),
    last_pull_at: Timestamp.nullable(),
    last_error: z.string().nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "IntegrationSubscription" });
export type IntegrationSubscription = z.infer<typeof IntegrationSubscription>;

/** A vendor call to make (integrations §2.5): queued, running, done, a dead letter, discarded. */
export const IntegrationJob = z
  .object({
    company_id: Uuid,
    id: Uuid,
    connection_id: Uuid.nullable(),
    owner_user_id: Uuid.nullable(),
    subscription_id: Uuid.nullable(),
    provider: z.string(),
    kind: IntegrationJobKind,
    key: z.string(),
    state: IntegrationJobState,
    attempts: z.int().min(0),
    next_at: Timestamp,
    lease_until: Timestamp.nullable(),
    last_error_class: IntegrationErrorClass.nullable(),
    last_error: z.string().nullable(),
    dead_reason: z.string().nullable(),
    done_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "IntegrationJob" });
export type IntegrationJob = z.infer<typeof IntegrationJob>;

/** One vendor call (integrations §2.9): never the request or the response body. */
export const IntegrationCall = z
  .object({
    company_id: Uuid,
    id: Uuid,
    connection_id: Uuid.nullable(),
    owner_user_id: Uuid.nullable(),
    provider: z.string(),
    tool: z.string(),
    family: z.string().nullable(),
    path_template: z.string(),
    method: z.string(),
    status: z.int().nullable(),
    error_class: IntegrationErrorClass.nullable(),
    duration_ms: z.int().min(0),
    units: z.int().min(0),
    actor: z.string(),
    run_id: Uuid.nullable(),
    approval_id: Uuid.nullable(),
    job_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "IntegrationCall" });
export type IntegrationCall = z.infer<typeof IntegrationCall>;

/** One thing that came in (build order §3 "One intake"); its payload is never in a list. */
export const InboundEvent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    source_kind: InboundSourceKind,
    source_key: z.string(),
    label: z.string(),
    verification: InboundVerification,
    dedupe_key: z.string().nullable(),
    delivery_id: Uuid.nullable(),
    subscription_id: Uuid.nullable(),
    owner_user_id: Uuid.nullable(),
    notice: z.boolean(),
    payload_bytes: z.int().min(0),
    payload_expires_at: Timestamp.nullable(),
    payload_purged_at: Timestamp.nullable(),
    screen: InboundDeliveryScreen,
    status: InboundEventStatus,
    drop_reason: z.string().nullable(),
    rule_id: Uuid.nullable(),
    handler_membership_id: Uuid.nullable(),
    handler_via: InboundHandlerVia.nullable(),
    task_id: Uuid.nullable(),
    job_id: Uuid.nullable(),
    /** The payload's file on the task, until the payload's retention ends. */
    payload_attachment_id: Uuid.nullable(),
    received_at: Timestamp,
    ...stamps,
  })
  .meta({ id: "InboundEvent" });
export type InboundEvent = z.infer<typeof InboundEvent>;

/** Per source a default handler DE and the owner's instructions (build order §3). */
export const InboundHandlerRule = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: InboundRuleKind,
    source_key: z.string(),
    handler_membership_id: Uuid.nullable(),
    owner_membership_id: Uuid.nullable(),
    instructions: z.string().nullable(),
    board_id: Uuid.nullable(),
    label: z.string().nullable(),
    enabled: z.boolean(),
    ...ActorShortColumns.shape,
    ...stamps,
  })
  .meta({ id: "InboundHandlerRule" });
export type InboundHandlerRule = z.infer<typeof InboundHandlerRule>;

/** `PUT intake/rules`: one rule per (kind, source key), made or replaced. */
export const InboundHandlerRulePut = z
  .strictObject({
    kind: InboundRuleKind,
    /** The source's id; the owner's membership id for `owner`; `default` for `company`. */
    source_key: z.string().trim().min(1).max(200),
    handler_membership_id: Uuid.nullable().optional(),
    owner_membership_id: Uuid.nullable().optional(),
    instructions: z.string().trim().min(1).max(LIMITS.longText).nullable().optional(),
    board_id: Uuid.nullable().optional(),
    label: z.string().trim().min(1).max(120).nullable().optional(),
    enabled: z.boolean().optional(),
  })
  .meta({ id: "InboundHandlerRulePut" });
export type InboundHandlerRulePut = z.infer<typeof InboundHandlerRulePut>;

/** What a notify door answers a vendor (202): accepted, the same notice again, or ignored. */
export const NotifyReceipt = z
  .object({
    status: z.enum(["accepted", "duplicate", "ignored"]),
    event_id: Uuid.nullable(),
  })
  .meta({ id: "NotifyReceipt" });
export type NotifyReceipt = z.infer<typeof NotifyReceipt>;
